import { isSubagentSessionKey } from "../../../../src/routing/session-key.js";
import type { GatewaySessionRow, SessionsListResult } from "../types.ts";
import { resolveChatTabLabel } from "./chat-tabs.ts";
import { resolveAgentIdForSession } from "./resolve-session-agent.ts";
import type { SessionStatus } from "./session-status.ts";
import { classifySessionCatalogKind } from "./session-catalog.ts";

export type ChatHistoryEntry = {
  key: string;
  title: string;
  preview: string;
  updatedAt: number;
  /**
   * Display kind for the row. Surfaced by `classifySessionCatalogKind`:
   * - `"main"`     → the agent's anchor session (`agent:<id>:main` or `agent:<id>:subagent:<uuid>` with `rest === "main"`)
   * - `"fork"`     → an explicit fork (`agent:<id>:fork:<uuid>`)
   * - `"subagent"` → a spawned subagent session — historically hidden, now shown per spec
   * - `"other"`    → anything else (legacy/legacy-key sessions)
   */
  kind: "main" | "fork" | "subagent" | "other";
  archived: boolean;
  isOpenTab: boolean;
  isActive: boolean;
  /**
   * JAISIU-3334 / UX-R10: mark rows whose key matches the
   * orchestration pattern (`agent:<id>:subagent:<id>`) AND have a
   * `spawnedBy` parent. The chat-history sidebar must drop them so the
   * operator's left rail is not polluted with every spawned subagent.
   * The session is still reachable via the parent's Children rail
   * (Pillar C, JAISIU-3308) and the Inspector — only the top-level
   * "Chats" navigator excludes it. Optional on the type so existing
   * call sites, serialized payloads, and tests that build entries
   * by hand do not need to thread a value through.
   */
  hiddenFromHistory?: boolean;
  /**
   * Local-only flag toggled by the sidebar's bulk-select UI
   * (JAISIU-3327 / UX-R2). Never set by `buildChatHistoryGroups` and
   * never persisted — it lives on the row only while the sidebar is in
   * select mode. Type-level optional so existing serialized payloads
   * and tests don't need to thread a value through.
   */
  selected?: boolean;
/**
   * Pinned-at-top flag toggled by the sidebar's star button
   * (JAISIU-3328 / UX-R3). NEVER populated by
   * `buildChatHistoryGroups` — only `partitionByFavorite` stamps it
   * (true on every entry that was pulled into the favorites bucket,
   * undefined otherwise). The sidebar reads this flag to drive the
   * `data-favorite="true|false"` attribute and the visible star
   * glyph. Kept optional so legacy callers, exported snapshots, and
   * existing tests don't need to thread a value through.
   */
  favorite?: boolean;
  /**
   * JAISIU-types-sweep: per-row subagent-status field that drives the
   * chip pill ("running", "done", "failed", "cancelled", …) on the
   * sidebar. Populated from `sessionMeta[key].status`, which
   * `app-render-fleet-chat.ts` fills from `computeSessionStatus` — i.e.
   * it really is a `SessionStatus`, so the field is declared as one.
   * It used to be a bare `string`, which forced the sidebar to lie about
   * its type (`as SessionStatus | undefined`) even though it already
   * guards with `status ? … : nothing`. Optional + nullable because not
   * every session is a subagent — main sessions carry no status, and
   * `sessionMeta` itself is optional.
   */
  status?: SessionStatus | null;
  /**
   * JAISIU-types-sweep: parent session key for a subagent session.
   * Resolved by `buildChatHistoryGroups` from
   * `GatewaySessionsRow.spawnedBy`. Optional: a main session has no
   * `parentKey`.
   */
  parentKey?: string | null;
  /**
   * JAISIU-types-sweep: live count of child sessions that this entry
   * has spawned (summed across fleet composer plans, cron plans,
   * direct tool invocations). Drives the chip "3 children" badge in
   * the sidebar and the badge color (zero / one / many children).
   * Optional on the type — only populated when the session is a
   * fleet composer parent that has live or recently-completed children.
   */
  subagentCount?: number | null;
  /**
   * JAISIU-types-sweep: short error message the runtime surfaced on
   * the last turn for the session, truncated to 240 chars by the
   * gateway before it lands in the catalog payload. Drives the chip
   * toast on the row. Optional — only present when the session's
   * last turn ended in failure.
   */
  lastError?: string | null;
  /**
   * JAISIU-types-sweep: human-readable model label (e.g. "Claude Sonnet 4.5")
   * used by the chip tooltip and the row's accessibility name. Optional —
   * legacy fixtures and snapshots that pre-date JAISIU-3x didn't carry it.
   */
  modelLabel?: string | null;
};

export type ChatHistoryTimeGroup = "today" | "yesterday" | "previous7" | "older";

export type ChatHistoryGroup = {
  id: ChatHistoryTimeGroup;
  label: string;
  entries: ChatHistoryEntry[];
};

const GROUP_LABELS: Record<ChatHistoryTimeGroup, string> = {
  today: "Today",
  yesterday: "Yesterday",
  previous7: "Previous 7 days",
  older: "Older",
};

/**
 * JAISIU-3334 / UX-R10 pure helper: returns `true` for session keys
 * that match the orchestration pattern (`agent:<id>:subagent:<id>`)
 * AND carry a non-empty `spawnedBy`.
 *
 * Why both conditions:
 *  - **Key shape**: `isSubagentSessionKey` matches the raw
    `subagent:<id>` legacy form as well as the
    `agent:<id>:subagent:<id>` form. Both shapes are used by
    `classifySessionCatalogKind` to badge the row as `subagent`.
  - **`spawnedBy` set**: distinguishes an orchestration-spawned
    child from a hand-forked session that happens to land in the
    same key namespace. A session the operator explicitly forked
    without a parent (`spawnedBy` empty) MUST still appear in the
    chat-history sidebar — only true children of an orchestrator
    are hidden.
 *
 * Pure function — no DOM, no state. Safe to call from `rowToEntry`,
 * from `filterVisibleChats`, and from unit tests directly.
 */
export function isOrchestrationChildKey(key: string, spawnedBy?: string | null): boolean {
  if (!isSubagentSessionKey(key)) {
    return false;
  }
  if (typeof spawnedBy !== "string") {
    return false;
  }
  return spawnedBy.trim().length > 0;
}

/**
 * JAISIU-3334 / UX-R10: drop orchestration children from the
 * chat-history sidebar.
 *
 * The chat-history sidebar (`buildChatHistoryGroups` →
 * `FleetChatHistorySidebar`) is a navigator over the operator's
 * top-level conversations. Sessions that are orchestration
 * children (matching the `agent:<id>:subagent:<id>` pattern AND
 * having a `spawnedBy` parent) crowd the list with auto-spawned
 * threads and bury the operator's real chats. Hide them here.
 *
 * The Children's rail (Pillar C, JAISIU-3308) and the Inspector
 * continue to surface these sessions — they are still accessible,
 * just not at the top level.
 *
 * Pure function over `ChatHistoryEntry[]`. Exported for unit
 * testing the partition contract end-to-end (orchestration
 * children hidden, root / non-orchestration chats visible).
 */
export function filterVisibleChats<E extends ChatHistoryEntry>(entries: readonly E[]): E[] {
  return entries.filter((e) => e.hiddenFromHistory !== true);
}

function startOfLocalDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function resolveChatHistoryTimeGroup(
  updatedAt: number,
  now = Date.now(),
): ChatHistoryTimeGroup {
  const day = startOfLocalDay(now);
  const updatedDay = startOfLocalDay(updatedAt);
  const diffDays = Math.floor((day - updatedDay) / 86_400_000);
  if (diffDays <= 0) {
    return "today";
  }
  if (diffDays === 1) {
    return "yesterday";
  }
  if (diffDays <= 7) {
    return "previous7";
  }
  return "older";
}

/**
 * Resolves the human-readable title for a session row in the chat-history
 * sidebar.
 *
 * Priority chain:
 *  1. `row.derivedTitle` — server-computed title from
 *     `displayName` → `subject` → `label` (only when `labelManuallySet`)
 *     → `entry.autoTitle` → `label` (transient fork default) → first
 *     user message → sessionId prefix. Set when the gateway runs
 *     `sessions.list({ includeDerivedTitles: true })`. The
 *     `entry.autoTitle` slot is the source of truth for "auto-title
 *     from first user message" — it is populated exactly once by
 *     `chat.send` on the first user message and never overwritten.
 *  2. `row.label` — operator-set label (the Rename action targets this).
 *     Wins over `derivedTitle` only when `labelManuallySet === true`,
 *     which is the only signal that the operator explicitly renamed
 *     this chat via the JSON-RPC Rename action. A non-empty `label`
 *     WITHOUT `labelManuallySet` is the temporary default label that
 *     `forkGatewaySession` writes on every "New chat" — it MUST NOT
 *     shadow the freshly-minted `autoTitle`.
 *  3. `row.displayName` — gateway-cached display name.
 *  4. `resolveChatTabLabel(...)` — sticky fallback (e.g. `Chat 1`,
 *     `Subagent 2`) used for transient states where the session row
 *     is missing or the gateway has not yet derived a title.
 *
 * Why `derivedTitle` first and not `label`: a renamed chat has both
 * `label` and (if the rename was after the title was first derived)
 * `derivedTitle` populated, but the operator's rename wins semantically.
 * `derivedTitle` is populated by the gateway and is what the sidebar
 * shows for brand-new sessions whose transcript-derived title beats the
 * synthetic `Chat N` fallback. When the operator renames, the gateway
 * patches the row's `label` (and sets `labelManuallySet = true`) and
 * the next refresh returns the renamed value through this same
 * function.
 */
/**
 * Resolves the human-readable title for a session row in the chat-history
 * sidebar.
 *
 * Exported for unit testing — the catalog-level
 * `buildChatHistoryGroups` test only asserts integration; the per-priority
 * behavior is best pinned by calling this function directly with a
 * crafted `GatewaySessionRow`.
 */
export function sessionTitle(row: GatewaySessionRow, tabIndex: number): string {
  const derived = row.derivedTitle?.trim();
  if (derived) {
    return derived;
  }
  const label = row.label?.trim();
  if (label) {
    return label;
  }
  const display = row.displayName?.trim();
  if (display) {
    return display;
  }
  return resolveChatTabLabel(row.key, tabIndex, null);
}

function sessionPreview(row: GatewaySessionRow): string {
  // 1. Prefer the frozen first-user preview (persisted once on the first
  //    chat.send and never overwritten by later turns). This is what
  //    `<span class="fleet-chat-history__item-preview muted">` binds to
  //    so the preview line does not keep updating as the conversation
  //    progresses. See `ensureSessionFirstUserPreview` in the gateway.
  const frozen = row.firstUserPreview?.trim();
  if (frozen) {
    return frozen.length > 200 ? `${frozen.slice(0, 197)}…` : frozen;
  }
  // 2. Legacy / pre-feature fallback: derive from the live transcript if
  //    the entry has no persisted preview yet (older sessions).
  // 3. Last resort: the latest-message preview (updates every turn - only
  //    shown when nothing else is available).
  const live = row.lastMessagePreview?.trim();
  if (live) {
    return live.length > 120 ? `${live.slice(0, 117)}…` : live;
  }
  return "";
}

function rowToEntry(
  row: GatewaySessionRow,
  params: {
    archivedKeys: Set<string>;
    openTabKeys: Set<string>;
    activeSessionKey: string;
    tabIndex: number;
  },
): ChatHistoryEntry | null {
  const key = row.key?.trim() ?? "";
  if (!key) {
    return null;
  }
  // Per spec: "Chats should represent saved conversation sessions." A
  // subagent session is just another saved session — the operator's most
  // recent work typically lives under `agent:<userId>:subagent:*` (and
  // historically the catalog hid those rows, which left the sidebar
  // empty for operators whose working agent has no top-level sessions).
  // Surface every non-empty key here; `kind` carries the disambiguating
  // badge for the view layer.
  const catalogKind = classifySessionCatalogKind(key);
  const kind: ChatHistoryEntry["kind"] =
    catalogKind === "fork"
      ? "fork"
      : catalogKind === "main"
        ? "main"
        : catalogKind === "subagent"
          ? "subagent"
          : "other";
  return {
    key,
    title: sessionTitle(row, params.tabIndex),
    preview: sessionPreview(row),
    updatedAt: row.updatedAt ?? 0,
    kind,
    archived: params.archivedKeys.has(key),
    isOpenTab: params.openTabKeys.has(key),
    isActive: key === params.activeSessionKey,
    // JAISIU-3334 / UX-R10: orchestration-spawned children are flagged
    // here and filtered out by `filterVisibleChats` (and by the
    // sidebar's `filterGroups` drop step). The Children's rail in the
    // Sessions Hub uses a separate catalog (`buildSessionCatalog.subagentsOfActive`)
    // and is NOT affected.
    hiddenFromHistory: isOrchestrationChildKey(key, row.spawnedBy),
  };
}

export function buildChatHistoryGroups(params: {
  activeSessionKey: string;
  openTabs: string[];
  archivedKeys: string[];
  sessions: SessionsListResult | null;
  includeArchived?: boolean;
  now?: number;
  /**
   * JAISIU-types-sweep: per-session live metadata (status, subagent
   * count, model label, error) that the chat-history sidebar stamps
   * onto each entry before grouping. Optional on the type so legacy
   * call sites that don't have a live metadata source keep working —
   * the catalog coalesces missing maps to `undefined` and the sidebar
   * falls back to placeholder chips.
   */
  sessionMeta?: Record<
    string,
    {
      status?: SessionStatus | null;
      subagentCount?: number | null;
      modelLabel?: string | null;
      lastError?: string | null;
      parentKey?: string | null;
    }
  >;
}): { active: ChatHistoryGroup[]; archived: ChatHistoryEntry[] } {
  const activeKey = params.activeSessionKey.trim();
  const archivedSet = new Set(params.archivedKeys.map((k) => k.trim()).filter(Boolean));
  const openTabSet = new Set(params.openTabs.map((k) => k.trim()).filter(Boolean));

  // Per spec: the chat-history sidebar is a navigator over "saved
  // conversation sessions" — one row per session, regardless of which
  // agent it belongs to. The agent context is restored on click
  // (`switchChatSession` activates the session; the key encodes the
  // agent). Filter only empties out.
  const rows = (params.sessions?.sessions ?? []).filter((row) => {
    const key = row.key?.trim() ?? "";
    return Boolean(key);
  });

  const entries: ChatHistoryEntry[] = [];
  let tabIndex = 0;
  for (const row of rows) {
    const entry = rowToEntry(row, {
      archivedKeys: archivedSet,
      openTabKeys: openTabSet,
      activeSessionKey: activeKey,
      tabIndex,
    });
    if (entry) {
      entries.push(entry);
      tabIndex += 1;
    }
  }

  entries.sort((a, b) => b.updatedAt - a.updatedAt);

  // JAISIU-3334 / UX-R10: partition step. The chat-history sidebar must
  // exclude orchestration children (sessions whose key matches
  // `agent:<id>:subagent:<id>` and have a `spawnedBy` parent). They are
  // auto-spawned threads, not operator-created chats, and including them
  // buries the operator's real conversations. Apply `filterVisibleChats`
  // here so the partition happens at the source — every consumer of
  // `buildChatHistoryGroups` (`buildFleetChatRenderProps`, the
  // sidebar, the agents-import panel) sees the filtered list without
  // having to re-filter.
  //
  // Children's rail (Pillar C) is unaffected — it consumes
  // `buildSessionCatalog` instead, which still surfaces
  // `subagentsOfActive` for the active session.
  const visibleEntries = filterVisibleChats(entries);
  const activeEntries = visibleEntries.filter((e) => !e.archived);
  const archivedEntries = visibleEntries.filter((e) => e.archived);

  const buckets = new Map<ChatHistoryTimeGroup, ChatHistoryEntry[]>();
  for (const g of Object.keys(GROUP_LABELS) as ChatHistoryTimeGroup[]) {
    buckets.set(g, []);
  }
  const now = params.now ?? Date.now();
  for (const entry of activeEntries) {
    const group = resolveChatHistoryTimeGroup(entry.updatedAt, now);
    buckets.get(group)?.push(entry);
  }

  const active: ChatHistoryGroup[] = (Object.keys(GROUP_LABELS) as ChatHistoryTimeGroup[])
    .map((id) => ({
      id,
      label: GROUP_LABELS[id],
      entries: buckets.get(id) ?? [],
    }))
    .filter((g) => g.entries.length > 0);

  return {
    active,
    archived: params.includeArchived === false ? [] : archivedEntries,
  };
}

export function resolveHistoryAgentId(sessionKey: string): string {
  return resolveAgentIdForSession(sessionKey);
}

/**
 * Bulk delete a list of chat sessions in parallel.
 *
 * Per spec (JAISIU-3327 / UX-R2): the operator can multi-select chat rows
 * in the sidebar and delete them in one shot. The gateway exposes a
 * per-session `sessions.delete` RPC, so "bulk" here is just a parallel
 * fan-out of single deletes — there is no dedicated bulk endpoint and
 * none is needed (sessions are independent).
 *
 * Contract:
 *  - `keys` is sanitized: blanks and duplicates are stripped. Empty input
 *    resolves to a no-op `{ requested: 0, deleted: 0, failed: 0 }`.
 *  - Deletions are dispatched in **parallel** via `Promise.allSettled`
 *    so a slow delete for one session does not block the others.
 *  - Failures are **tolerated**: a single session failing (e.g. main
 *    session protection, transient RPC error) does not abort the rest.
 *    The returned counts let the caller surface a partial-success
 *    message and refetch the catalog.
 *  - The actual RPC call is injected as `deleteFn` so this module stays
 *    pure (no `state.client` import) and unit-testable. In production
 *    `deleteFn` is `(key) => state.client.request("sessions.delete", { key, deleteTranscript: true })`.
 *
 * Exported for unit testing.
 */
export type BulkDeleteChatsResult = {
  requested: number;
  deleted: number;
  failed: number;
};

export async function bulkDeleteChats(
  keys: readonly string[],
  deleteFn: (key: string) => Promise<unknown>,
): Promise<BulkDeleteChatsResult> {
  const sanitized = Array.from(
    new Set(keys.map((k) => (typeof k === "string" ? k.trim() : "")).filter(Boolean)),
  );
  if (sanitized.length === 0) {
    return { requested: 0, deleted: 0, failed: 0 };
  }
  const settled = await Promise.allSettled(sanitized.map((key) => deleteFn(key)));
  let deleted = 0;
  let failed = 0;
  for (let i = 0; i < settled.length; i += 1) {
    const r = settled[i];
    if (r.status === "fulfilled") {
      deleted += 1;
    } else {
      failed += 1;
      // Swallow the per-key error; the aggregate count is the contract.
      // `sanitized[i]` is the key that failed, in case the caller wants
      // to log it — but this helper does not log to keep it pure.
      void sanitized[i];
    }
  }
  return { requested: sanitized.length, deleted, failed };
}

/**
 * Pure helper — splits the pre-built `ChatHistoryGroup[]` (already sorted by
 * `updatedAt desc` and bucketed by day) into:
 *
 *   - `favorites`: every entry whose key is in `favoritedKeys`, sorted by
 *     `updatedAt desc`. This is what the sidebar renders inside the "Pinned"
 *     section at the top of the list (UX-R3 favorite chats, JAISIU-3328).
 *
 *   - `byDay`: the same day groups minus any entry that was pulled into
 *     `favorites`. Empty groups are dropped so the view can iterate without
 *     an extra `.length > 0` filter.
 *
 * Determinism: the function is pure — same `(groups, favoritedKeys)` always
 * yields the same `{ favorites, byDay }`. No mutation of `groups`.
 *
 * Exported for unit testing.
 */
export function partitionByFavorite(
  groups: ChatHistoryGroup[],
  favoritedKeys: ReadonlySet<string>,
): { favorites: ChatHistoryEntry[]; byDay: ChatHistoryGroup[] } {
  const hasFavorites = favoritedKeys != null && favoritedKeys.size > 0;
  if (!hasFavorites) {
    // No favorites — short-circuit: favorites is empty, byDay mirrors the
    // input groups with empty buckets dropped so the view can iterate
    // without a separate `g.entries.length > 0` filter. We deliberately
    // do NOT stamp `favorite: true` onto the entries here: only starred
    // rows carry that flag, per the `ChatHistoryEntry.favorite` contract.
    return {
      favorites: [],
      byDay: groups.filter((g) => g.entries.length > 0),
    };
  }
  const favorites: ChatHistoryEntry[] = [];
  const byDay: ChatHistoryGroup[] = [];
  for (const group of groups) {
    const remaining: ChatHistoryEntry[] = [];
    for (const entry of group.entries) {
      if (favoritedKeys.has(entry.key)) {
        // Stamp `favorite: true` so the sidebar can render the star
        // glyph + `data-favorite="true"` without re-running the
        // membership check. By contract only entries pulled into
        // `favorites` carry this flag — `byDay` rows leave it
        // undefined, matching the type's optional shape.
        favorites.push({ ...entry, favorite: true });
      } else {
        remaining.push(entry);
      }
    }
    if (remaining.length > 0) {
      byDay.push({ id: group.id, label: group.label, entries: remaining });
    }
  }
  // Favorites are surfaced most-recent-first, matching the
  // updatedAt-desc convention used by `buildChatHistoryGroups`. The
  // byDay groups already preserve their input ordering because we walk
  // groups in the order they were given.
  favorites.sort((a, b) => b.updatedAt - a.updatedAt);
  return { favorites, byDay };
}
